// The montage of a speaker turn, as pure functions over samples and word
// timings — one code for the server (tasks/drafts.js, tasks/tools.js) and
// the browser (the Assembly panel builds the Draft it plays right here, and
// Accept on the server rebuilds it with these same functions; both decode
// Opus at its own 48 kHz, so the two come out equal to the last bit but one).
// CommonJS, no Node APIs, nothing async: whoever calls hands over the decoded
// samples and the timings, and gets samples, seams and words back.
//
// Two layers:
//   the range maths — baseRangeFor (which seconds of a take read these
//     words), spliceSamples (one piece of another take into a base);
//   the Draft — its state (segments, cuts, pauses, gain: applyDraftOp and the
//     helpers that place a range in the turn's body as it reads now) and its
//     sound (composeTurn: the base's turn with every range spliced in;
//     spliceTurnInto: that turn put back into the whole take).

const ops = require('./sampleOps');
const loud = require('./loudness');
const pausesLib = require('./pauses');
const { textWords, matchWords, alignedForms } = require('./wordMap');
const { spliceWords, pieceWordsFrom, cutWordsMany } = require('./alignEdit');

const FADE_SEC = 0.015;          // the studio's default crossfade
const EDGE_SEC = 0.15;           // how far a cut keeps away from the neighbouring word
const CUT_LEAD_SEC = 0.08;       // what stays of the pause in front of the word after a cut (as remove_intro)
const SEAM_PAD_SEC = 1.5;        // how much plays around a seam
const CONTEXT_SEC = 3;           // how much of the untouched take plays around a Before/After
const PAUSE_DEFAULTS = { threshold: 0.02, minSec: 0.3, caps: { sentence: 0.3, phrase: 0.2, lead: 0.3, tail: 0.3, any: 0.3 } };

const round3 = (x) => Math.round(x * 1000) / 1000;
const need = (cond, msg) => { if (!cond) { const e = new Error(msg); e.code = 'EPARAM'; throw e; } };

// ----- the range maths -------------------------------------------------------------

// Where words of `text` sit among the words of an alignment measured on a
// possibly older wording: -> map[textWordIndex] = alignmentWordIndex | -1.
function mapTextToAlignment(text, al) {
  const cur = textWords(text);
  return { cur, map: matchWords(cur.map((w) => w.n), alignedForms(al.text, al.words)) };
}

// The stretch of the base take that reads text[start, end): the first and
// last aligned words of that span and the seconds they cover, widened by a
// hair into the pauses around them but never into the neighbouring words.
//
// Words the take does not read — the text was edited after it was voiced
// («8 possibilities» became «эйт посибилитис») — are placed BY PLACE: the
// stretch between the nearest words before and after the span that the take
// does read is what it said there, and that is what a range replaces
// (`byPlace`, with `said`, the take's own words there). Nothing between
// them: the take said nothing there, and a range is inserted at that point
// (`insert`, a === b). A take that reads nothing around the span cannot be
// placed and refuses.
function baseRangeFor(text, start, end, al) {
  const { cur, map } = mapTextToAlignment(text, al);
  // A word is in the span when its letters are: the span may stop before the
  // punctuation the word's bounds carry («форпост.»).
  const inSpan = (w) => w.a >= start && (w.b <= end || (w.a < end && /^[^\p{L}\p{N}]*$/u.test(text.slice(end, w.b))));
  const inside = cur.map((w, i) => (inSpan(w) ? i : -1)).filter((i) => i >= 0);
  need(inside.length, 'The span holds no words');
  const mapped = inside.map((i) => map[i]).filter((k) => k >= 0);
  const words = al.words;
  if (mapped.length < Math.max(1, Math.floor(inside.length * 0.6))) {
    // by place: between the nearest read words around the span
    let prevJ = -1;
    for (let k = inside[0] - 1; k >= 0; k -= 1) if (map[k] >= 0) { prevJ = map[k]; break; }
    let nextJ = words.length;
    for (let k = inside[inside.length - 1] + 1; k < cur.length; k += 1) if (map[k] >= 0) { nextJ = map[k]; break; }
    need(prevJ >= 0 || nextJ < words.length, 'The take reads none of the words around this range (the text changed since it was voiced) — it needs a full re-take, not a splice');
    const lo = prevJ + 1;
    const hi = nextJ - 1;
    if (hi >= lo) {
      const a = Math.max(prevJ >= 0 ? words[prevJ].e : 0, words[lo].s - EDGE_SEC, 0);
      const b = Math.min(nextJ < words.length ? words[nextJ].s : Infinity, words[hi].e + EDGE_SEC);
      return { lo, hi, a: round3(a), b: round3(b), missing: inside.length - mapped.length, byPlace: true, insert: false, said: al.text.slice(words[lo].a, words[hi].b) };
    }
    const point = prevJ >= 0 && nextJ < words.length ? (words[prevJ].e + words[nextJ].s) / 2 : prevJ >= 0 ? words[prevJ].e : words[nextJ].s;
    return { lo, hi, a: round3(point), b: round3(point), missing: inside.length - mapped.length, byPlace: true, insert: true, said: '' };
  }
  const lo = Math.min(...mapped);
  const hi = Math.max(...mapped);
  const prevEnd = lo > 0 ? words[lo - 1].e : 0;
  const nextStart = hi + 1 < words.length ? words[hi + 1].s : Infinity;
  const a = Math.max(prevEnd, words[lo].s - EDGE_SEC, 0);
  const b = Math.min(nextStart, words[hi].e + EDGE_SEC);
  return { lo, hi, a: round3(a), b: round3(b), missing: inside.length - mapped.length, byPlace: false };
}

// The piece of a donor whose words of the range have estimated times only
// (carried over from another take that never read them: confidence 0): its
// bounds are not to be trusted, so the whole stretch between the nearest
// confident words around the range is taken. -> {flo, fhi, sa, sb} | null
const ESTIMATED_C = 0.1;
function widenEstimatedPiece(al, lo, hi, duration) {
  const w = al.words;
  if (!w.slice(lo, hi + 1).every((x) => !(x.c > ESTIMATED_C))) return null;
  let p = lo - 1; while (p >= 0 && !(w[p].c > ESTIMATED_C)) p -= 1;
  let n = hi + 1; while (n < w.length && !(w[n].c > ESTIMATED_C)) n += 1;
  return { flo: lo, fhi: hi, sa: p >= 0 ? w[p].e : 0, sb: n < w.length ? w[n].s : duration };
}

// Replace [a, b] of the base take (its words lo..hi) with a piece of another
// take: the whole voiced part of a fragment take, or — with `piece` — only
// its words flo..fhi, which is how one phrase or one word is carried over
// out of a whole re-voiced turn (or out of another point's take).
// -> {samples, rate, words, pieceDur, fadeSec, a, b, sa, sb, newEnd, pieceWords, carried, droppedWords}
function spliceSamples({ base, baseAlign, frag, fragAlign, a, b, lo, hi, piece = null }, rate) {
  need(Number.isFinite(rate) && rate > 0, 'spliceSamples needs the sample rate');
  const fw = fragAlign.words;
  need(fw.length, 'The fragment take has no words in its timings');
  const flo = piece ? piece.flo : 0;
  const fhi = piece ? piece.fhi : fw.length - 1;
  need(flo >= 0 && fhi >= flo && fhi < fw.length, 'The piece names words the fragment take does not have');
  // The piece: from a hair before its first word to a hair after its last,
  // never into the neighbouring words of the fragment.
  const prevEnd = flo > 0 ? fw[flo - 1].e : 0;
  const nextStart = fhi + 1 < fw.length ? fw[fhi + 1].s : Infinity;
  // `piece.sa`/`piece.sb`: bounds given by the caller (widenEstimatedPiece)
  const sa = piece && piece.sa != null ? Math.max(0, piece.sa) : Math.max(0, prevEnd, fw[flo].s - EDGE_SEC);
  const sb = piece && piece.sb != null ? Math.min(frag.length / rate, piece.sb) : Math.min(frag.length / rate, nextStart, fw[fhi].e + EDGE_SEC);
  need(sb - sa >= 0.15, `The piece is only ${round3(sb - sa)}s long — too short to splice cleanly`);
  const cut = ops.trimTo(frag, rate, sa, sb);
  need(ops.peakOf(cut, rate) > 0.01, 'The piece of the fragment take is silent');
  const { out, fadeSec } = ops.spliceCrossfade(base, rate, a, b, cut, FADE_SEC);
  const pieceDur = cut.length / rate;
  // The piece's words, given the base words' char spans: fragment words
  // flo..fhi matched to base words lo..hi by wording.
  const fragSlice = fw.slice(flo, fhi + 1);
  const baseSlice = baseAlign.words.slice(lo, hi + 1);
  const m = matchWords(alignedForms(fragAlign.text, fragSlice), alignedForms(baseAlign.text, baseSlice));
  const toEdit = (di) => (di >= flo && di <= fhi && m[di - flo] >= 0 ? lo + m[di - flo] : -1);
  const pieceWords = pieceWordsFrom(fw, baseAlign.words, sa, sb, toEdit);
  const words = spliceWords(baseAlign.words, a, b, pieceDur, fadeSec, pieceWords);
  return { samples: out, rate, words, pieceDur: round3(pieceDur), fadeSec, a, b, sa: round3(sa), sb: round3(sb), newEnd: round3(a - fadeSec + pieceDur - fadeSec), pieceWords, carried: pieceWords.length, droppedWords: fragSlice.length - pieceWords.length };
}

// ----- the Draft's state ------------------------------------------------------------
// State of a Draft (quest_turn_drafts.state):
//   {base: {versionId}, segments: [{id, start, end, text, versionId, by, at}],
//    cuts: [{id, start, end, text, by, at}], pauses: {caps, minSec} | null,
//    gainDb: number | null, manualTakes: [versionId]}
//   `start`/`end` are offsets inside the turn's BODY; `text` is the exact
//   wording of the range, so a range is found again when the body shifts and
//   refused when it is gone.

// A range of the turn's text, named by exact wording, found in the body now.
// -> {start, end} | {missing: true} | {start, end, ambiguous: n}
function findRange(body, seg) {
  const exact = String(seg.text || '');
  if (!exact) return { missing: true };
  // whole words only: «в» must not land on the first letter of «временем»
  // (the wording may carry a space at either end; the letters inside count)
  const lead = exact.length - exact.trimStart().length;
  const trail = exact.length - exact.trimEnd().length;
  const letter = (ch) => ch != null && /[\p{L}\p{N}́]/u.test(ch);
  const wholeWord = (k) => !letter(body[k + lead - 1]) && !letter(body[k + exact.length - trail]);
  if (body.slice(seg.start, seg.end) === exact && wholeWord(seg.start)) return { start: seg.start, end: seg.end };
  const hits = [];
  let k = body.indexOf(exact);
  while (k >= 0) { if (wholeWord(k)) hits.push(k); k = body.indexOf(exact, k + 1); }
  if (hits.length === 1) return { start: hits[0], end: hits[0] + exact.length };
  if (hits.length > 1) {
    // the one closest to where it was
    const best = hits.reduce((a, b) => (Math.abs(b - seg.start) < Math.abs(a - seg.start) ? b : a));
    return { start: best, end: best + exact.length, ambiguous: hits.length };
  }
  return { missing: true };
}

// The segments of a state placed in the body as it reads now, in text order,
// each with where it stands (`start`/`end`) or why it cannot (`missing`).
function resolveSegments(body, state) {
  return (state.segments || []).map((s) => ({ ...s, ...findRange(body, s) })).sort((a, b) => (a.start || 0) - (b.start || 0));
}

// The cuts of a state placed in the body as it reads now (as resolveSegments).
function resolveCuts(body, state) {
  return (state.cuts || []).map((c) => ({ ...c, ...findRange(body, c) })).sort((a, b) => (a.start || 0) - (b.start || 0));
}

// Snap a character range of the body to whole words. -> {start, end, text} | null
function snapToWords(body, start, end) {
  const ws = textWords(body);
  const inside = ws.filter((w) => w.b > start && w.a < end);
  if (!inside.length) return null;
  const a = inside[0].a;
  const b = inside[inside.length - 1].b;
  return { start: a, end: b, text: body.slice(a, b) };
}

// Add a replacement to a state: overlapping segments give way (a range is
// taken from exactly one take). What sticks out of the new range on either
// side stays, snapped to whole words of the body — a remainder that holds no
// word is dropped, so no segment ever names a space or a piece of a word
// (such a stub, «в» cut off «временем», once broke every preview of a Draft).
// -> the new segments list
function withSegment(state, body, seg) {
  const keep = [];
  for (const s of state.segments || []) {
    if (s.end <= seg.start || s.start >= seg.end) { keep.push(s); continue; }
    if (s.start < seg.start) { const left = snapToWords(body, s.start, seg.start); if (left) keep.push({ ...s, ...left }); }
    if (s.end > seg.end) { const right = snapToWords(body, seg.end, s.end); if (right) keep.push({ ...s, ...right }); }
  }
  return keep.filter((s) => s.text && s.end > s.start).concat([seg]).sort((a, b) => a.start - b.start);
}

// Add a cut to a state: a cut that touches another is merged with it into one
// range of the body (so the sound is dropped once). -> the new cuts list
function withCut(state, body, cut) {
  let { start, end } = cut;
  const keep = [];
  for (const c of state.cuts || []) {
    if (c.end < start || c.start > end) { keep.push(c); continue; }
    start = Math.min(start, c.start); end = Math.max(end, c.end);
  }
  return keep.concat([{ ...cut, start, end, text: body.slice(start, end) }]).sort((a, b) => a.start - b.start);
}

// Time spans sorted and merged where they touch, so each is cut once.
function mergeSpans(spans) {
  const out = [];
  for (const s of [...spans].sort((x, y) => x.start - y.start)) {
    const last = out[out.length - 1];
    if (last && s.start <= last.end + 1e-6) last.end = Math.max(last.end, s.end);
    else out.push({ ...s });
  }
  return out;
}

// One change of a Draft's state, the same on the server (after it checked
// the take named is a take of this field with timings) and in the browser
// (which plays the result at once and sends the op on). `undo` is not here:
// it is a read of the revision before (the server) or of the state kept
// before (the browser). -> {state, what}
//   {op: 'replace', start, end, versionId, isBase}  words [start, end) of the body from a take; from the base itself: the range reads from the base again
//   {op: 'remove', segmentId}
//   {op: 'cut', start, end} / {op: 'uncut', cutId}
//   {op: 'base', versionId} / {op: 'manual', versionId}
//   {op: 'pauses', on, caps?, minSec?} / {op: 'gain', gainDb}
function applyDraftOp(state0, op, { body, by, at = new Date().toISOString(), newId, stampOf = (id) => `#${id}` }) {
  const state = { ...state0 };
  let what = '';
  const snap = () => {
    if (!body) throw new Error('The turn is not in the text any more');
    const snapped = snapToWords(body, Number(op.start), Number(op.end));
    if (!snapped) throw new Error('The range holds no words');
    return snapped;
  };
  if (op.op === 'replace') {
    const snapped = snap();
    const id = Number(op.versionId);
    const seg = { id: newId(), start: snapped.start, end: snapped.end, text: snapped.text, versionId: id, by, at };
    if (op.isBase || id === Number(state.base.versionId)) {
      state.segments = withSegment(state, body, seg).filter((s) => s.id !== seg.id);
      what = `«${snapped.text}» reads from the base again`;
    } else {
      state.segments = withSegment(state, body, seg);
      what = `«${snapped.text}» from ${stampOf(id)}`;
    }
  } else if (op.op === 'remove') {
    const s = (state.segments || []).find((x) => x.id === op.segmentId);
    if (!s) throw new Error('No such range in the Draft');
    state.segments = (state.segments || []).filter((x) => x.id !== op.segmentId);
    what = `«${s.text}» reads from the base again`;
  } else if (op.op === 'cut') {
    const snapped = snap();
    if (snapToWords(body, 0, body.length).text === snapped.text) throw new Error('The range is the whole turn: nothing would be left to hear — re-voice or edit the text instead');
    state.cuts = withCut(state, body, { id: newId(), start: snapped.start, end: snapped.end, text: snapped.text, by, at });
    what = `«${snapped.text}» cut out of the sound (the text keeps the words)`;
  } else if (op.op === 'uncut') {
    const c = (state.cuts || []).find((x) => x.id === op.cutId);
    if (!c) throw new Error('No such cut in the Draft');
    state.cuts = (state.cuts || []).filter((x) => x.id !== op.cutId);
    what = `«${c.text}» sounds again`;
  } else if (op.op === 'base') {
    state.base = { versionId: Number(op.versionId) };
    what = `base: ${stampOf(Number(op.versionId))}`;
  } else if (op.op === 'manual') {
    state.base = { versionId: Number(op.versionId) };
    state.segments = [];
    state.manualTakes = [...new Set([...(state.manualTakes || []), Number(op.versionId)])];
    what = `studio edit saved as take #${Number(op.versionId)}; it is the base now`;
  } else if (op.op === 'pauses') {
    state.pauses = op.on === false ? null : { caps: op.caps && typeof op.caps === 'object' ? op.caps : PAUSE_DEFAULTS.caps, minSec: Number.isFinite(op.minSec) ? op.minSec : PAUSE_DEFAULTS.minSec };
    what = state.pauses ? 'overlong pauses shortened' : 'pauses as recorded';
  } else if (op.op === 'gain') {
    const g = op.gainDb == null ? null : Number(op.gainDb);
    if (g != null && !(Number.isFinite(g) && Math.abs(g) <= 12)) throw new Error('gainDb must be within ±12');
    state.gainDb = g;
    what = g == null ? 'level as recorded' : `level ${g > 0 ? '+' : ''}${g} dB`;
  } else throw new Error(`Unknown Draft operation «${op.op}»`);
  return { state, what };
}

// ----- the Draft's sound --------------------------------------------------------------

// The assembled turn: the base's turn with every segment spliced in, the cuts
// dropped, pauses shortened and the level set when asked. Synchronous over
// what the caller decoded: `samplesOf(versionId)` -> Float32Array at `rate`,
// `alignmentOf(versionId)` -> {text, words} (both must answer for the base
// and every segment's take — the caller checked the timings exist).
//   text      the field's whole voiced text (what the timings were measured on)
//   loc       {body, bodyStart, bodyEnd, changed} — the turn in that text now
//   turnName  how a warning names the turn
// -> {samples, rate, words (turn-local, spans into `text`), text, baseVersionId,
//     seams: [{at, segmentId, side}], placed, cuts, removedSec, pauseCuts, gain,
//     baseRange, duration, warnings}
function composeTurn({ rate, text, loc, state, samplesOf, alignmentOf, turnName = 'the turn' }) {
  if (!loc.body) throw new Error('The turn is not in the text any more');
  const warnings = [];
  if (loc.changed) warnings.push(`${turnName}: the words of the turn changed since the Draft was started — the assembly is matched to the new words as far as they coincide`);
  const baseId = Number(state.base.versionId);
  const alB = alignmentOf(baseId);
  need(alB, `The base take #${baseId} has no timings yet`);
  const samplesB = samplesOf(baseId);
  const rangeB = baseRangeFor(text, loc.bodyStart, loc.bodyEnd, alB);
  let samples = ops.trimTo(samplesB, rate, rangeB.a, rangeB.b);
  let words = alB.words.slice(rangeB.lo, rangeB.hi + 1).map((w) => ({ ...w, s: round3(w.s - rangeB.a), e: round3(w.e - rangeB.a) }));
  const seams = [];
  const placed = [];
  for (const seg of resolveSegments(loc.body, state)) {
    if (seg.missing) { warnings.push(`«${seg.text}» is no longer in the turn's text: this range is left out of the assembly`); continue; }
    if (seg.ambiguous) warnings.push(`«${seg.text}» stands ${seg.ambiguous} times in the turn: the one nearest its old place is used`);
    const alS = alignmentOf(Number(seg.versionId));
    need(alS, `Take #${seg.versionId} has no timings yet`);
    const cur = { text: alB.text, words };
    let r; let fr;
    try {
      r = baseRangeFor(text, loc.bodyStart + seg.start, loc.bodyStart + seg.end, cur);
      fr = baseRangeFor(text, loc.bodyStart + seg.start, loc.bodyStart + seg.end, alS);
    } catch (e) {
      // one range that cannot be placed leaves the assembly, it does not take the whole preview down
      warnings.push(`«${seg.text}» (from #${seg.versionId}) is left out of the assembly: ${e.message}`);
      continue;
    }
    if (fr.byPlace) { warnings.push(`«${seg.text}» (from #${seg.versionId}) is left out of the assembly: that take does not read these words`); continue; }
    const frag = samplesOf(Number(seg.versionId));
    const widened = widenEstimatedPiece(alS, fr.lo, fr.hi, frag.length / rate);
    if (r.byPlace) warnings.push(r.insert ? `«${seg.text}»: the base said nothing here, the range from #${seg.versionId} is inserted` : `«${seg.text}»: the base read other words here («${r.said}»), replaced by the range from #${seg.versionId}`);
    if (widened) warnings.push(`«${seg.text}» from #${seg.versionId}: the times of these words are estimates, so the whole stretch between the neighbouring words is taken`);
    const sp = spliceSamples({ base: samples, baseAlign: cur, frag, fragAlign: alS, a: r.a, b: r.b, lo: r.lo, hi: r.hi, piece: widened || { flo: fr.lo, fhi: fr.hi } }, rate);
    samples = sp.samples;
    words = sp.words;
    seams.push({ at: round3(sp.a), segmentId: seg.id, side: 'in' }, { at: round3(sp.newEnd), segmentId: seg.id, side: 'out' });
    placed.push({ segmentId: seg.id, text: seg.text, versionId: Number(seg.versionId), from: { start: round3(fr.a), end: round3(fr.b) }, at: round3(sp.a), end: round3(sp.newEnd), carried: sp.carried, dropped: sp.droppedWords });
  }
  // spans dropped from the assembly: the seams and the placed ranges move up by what was cut before them
  const dropSpans = (spans) => {
    samples = ops.deleteRegions(samples, rate, spans);
    words = cutWordsMany(words, spans);
    const cutBefore = (t) => spans.filter((c) => c.end <= t + 1e-6).reduce((n, c) => n + (c.end - c.start), 0);
    for (const s of seams) s.at = round3(s.at - cutBefore(s.at));
    for (const p of placed) { p.at = round3(p.at - cutBefore(p.at)); p.end = round3(p.end - cutBefore(p.end)); }
  };
  // the cuts: the words of each range go, together with the pause after them
  // up to CUT_LEAD_SEC before the next word (a cut at the start of the turn
  // takes the lead-in too), so no double pause is left behind
  const cuts = [];
  const cutSpans = [];
  for (const c of resolveCuts(loc.body, state)) {
    if (c.missing) { warnings.push(`«${c.text}» is no longer in the turn's text: this cut is left out of the assembly`); continue; }
    if (c.ambiguous) warnings.push(`«${c.text}» stands ${c.ambiguous} times in the turn: the one nearest its old place is cut`);
    const cur = { text: alB.text, words };
    const r = baseRangeFor(text, loc.bodyStart + c.start, loc.bodyStart + c.end, cur);
    const live = (w) => !w.d;
    const next = words.slice(r.hi + 1).find(live);
    const prev = words.slice(0, r.lo).filter(live).pop();
    const start = prev ? r.a : 0;
    const end = next ? Math.max(words[r.hi].e, next.s - CUT_LEAD_SEC) : samples.length / rate;
    cutSpans.push({ start, end });
    cuts.push({ id: c.id, text: c.text, start: round3(start), end: round3(end) });
  }
  const cutsMerged = mergeSpans(cutSpans);
  if (cutsMerged.length) dropSpans(cutsMerged);
  let pauseCuts = [];
  if (state.pauses) {
    const duration = samples.length / rate;
    const { frames } = pausesLib.levelFrames(samples, rate);
    const found = pausesLib.findPauses(frames, { threshold: PAUSE_DEFAULTS.threshold, minSec: state.pauses.minSec || PAUSE_DEFAULTS.minSec, from: 0, to: duration, duration });
    pauseCuts = pausesLib.planPauseCuts(found, words, alB.text, state.pauses.caps || PAUSE_DEFAULTS.caps, duration);
    if (pauseCuts.length) dropSpans(pauseCuts);
  }
  let gain = null;
  if (state.gainDb != null && state.gainDb !== 0) {
    const peak = loud.speechLevel(samples, rate).peakDb;
    const g = loud.limitGainForPeak(state.gainDb, peak);
    const out = loud.applyGains(samples, rate, [{ start: 0, end: samples.length / rate, gainDb: g }]);
    samples = out.samples;
    gain = { askedDb: state.gainDb, gainDb: g, limited: g !== state.gainDb };
  }
  return { samples, rate, words, text: alB.text, baseVersionId: baseId, seams, placed, cuts, removedSec: round3(cutsMerged.reduce((n, c) => n + (c.end - c.start), 0)), pauseCuts: pauseCuts.map((c) => ({ start: round3(c.start), end: round3(c.end) })), gain, baseRange: rangeB, duration: round3(samples.length / rate), warnings };
}

// The assembled turn spliced into the take active now (its other turns
// untouched) — what Accept stores, what the context preview plays.
//   activeSamples, activeAlign  the active take decoded, and its timings
// -> {samples, rate, words, text, range (of the turn in the active take), newEnd}
function spliceTurnInto({ rate, text, loc, assembled, activeSamples, activeAlign }) {
  const range = baseRangeFor(text, loc.bodyStart, loc.bodyEnd, activeAlign);
  const live = assembled.words.filter((w) => !w.d);
  need(live.length, 'The assembled turn has no voiced words');
  const sp = spliceSamples({ base: activeSamples, baseAlign: activeAlign, frag: assembled.samples, fragAlign: { text: assembled.text, words: assembled.words }, a: range.a, b: range.b, lo: range.lo, hi: range.hi, piece: null }, rate);
  return { samples: sp.samples, rate, words: sp.words, text: activeAlign.text, range, newEnd: sp.newEnd };
}

// A summary of an assembly that two sides compare: the decisions (where each
// range sits, the seams, the cuts, the length) exactly, and the sound by a
// coarse fingerprint. Two builds of one Draft from the same takes agree on
// the decisions always; the samples may differ by the last bit of a 16-bit
// WAV, so the fingerprint is the sum of the 16-bit values over 1 s windows,
// rounded — `samplesAgree` tells whether two fingerprints are within that bit.
function assemblySummary(asm) {
  const win = Math.max(1, Math.round(asm.rate));
  const sums = [];
  for (let i = 0; i < asm.samples.length; i += win) {
    let s = 0;
    const end = Math.min(asm.samples.length, i + win);
    for (let k = i; k < end; k += 1) s += Math.round(Math.max(-1, Math.min(1, asm.samples[k])) * 32767);
    sums.push(s);
  }
  return {
    rate: asm.rate,
    length: asm.samples.length,
    baseVersionId: asm.baseVersionId,
    seams: asm.seams.map((s) => ({ at: s.at, side: s.side, segmentId: s.segmentId })),
    placed: asm.placed.map((p) => ({ segmentId: p.segmentId, versionId: p.versionId, at: p.at, end: p.end })),
    cuts: asm.cuts.map((c) => ({ start: c.start, end: c.end })),
    pauseCuts: asm.pauseCuts,
    gain: asm.gain,
    sums,
  };
}

// Do two summaries describe the same assembly? -> {same, why}
function summariesAgree(a, b) {
  const dec = (s) => JSON.stringify([s.rate, s.length, s.baseVersionId, s.seams, s.placed, s.cuts, s.pauseCuts, s.gain]);
  if (dec(a) !== dec(b)) return { same: false, why: 'the decisions differ (ranges, seams, cuts or length)' };
  if (a.sums.length !== b.sums.length) return { same: false, why: 'the sound differs in length' };
  // one bit of 16-bit per sample at most, over a window of `rate` samples
  const tol = a.rate;
  for (let i = 0; i < a.sums.length; i += 1) if (Math.abs(a.sums[i] - b.sums[i]) > tol) return { same: false, why: `the sound differs at ${i}–${i + 1} s beyond the last bit` };
  return { same: true, why: '' };
}

module.exports = {
  FADE_SEC, EDGE_SEC, CUT_LEAD_SEC, SEAM_PAD_SEC, CONTEXT_SEC, PAUSE_DEFAULTS,
  mapTextToAlignment, baseRangeFor, spliceSamples, widenEstimatedPiece,
  findRange, resolveSegments, resolveCuts, snapToWords, withSegment, withCut, mergeSpans, applyDraftOp,
  composeTurn, spliceTurnInto, assemblySummary, summariesAgree,
};
